import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { Integration, License } from 'lib/types';
import { removeError } from 'reducers/integrations/actions';

import { RootState } from 'reducers';
import {
    createLicenseThunk,
    resetCreateOrUpdateLicenseStatus,
    updateLicenseThunk,
} from 'reducers/integrations/actions';
import { Status } from 'reducers/enum';

import Button from 'components/Button/Button';
import TextInput from 'components/TextInput/TextInput';
import Select from 'components/Select/Select';
import LoaderOverlay from 'components/LoaderOverlay/LoaderOverlay';

export default function LicenseEditor(props: LicenseEditorProps) {
    const { integration, license, isNewLicense } = props;
    const { id: integrationId, licenseTypes } = integration;

    const dispatch = useDispatch();
    const createOrUpdateLicenseStatus = useSelector(
        (state: RootState) => state.integrations.createOrUpdateLicenseStatus
    );
    const accountId = useSelector(
        (state: RootState) => state.credentials.accountId
    );
        
    const error = useSelector((state: RootState) => state.integrations.error);

    const navigate = useNavigate();

    const validationSchema = yup.object({
        softwareLicenseCode: yup
            .string()
            .required('Software License Code is required.'),
        licenseType: yup.string().required('License type is required.'),
        count: yup
            .number()
            .integer('Count must be a positiver integer or empty.')
            .positive('Count must be a positive integer or empty.')
            .typeError('Count must be a positive integer or empty.'),
        expireDate: yup
            .date()
            .nullable()
            .typeError('Expire date must be a date or empty.'),
        customData: yup.string().nullable(),
    });

    const handleOnSubmit = (values: LicenseFormikValues) => {
        const {
            softwareLicenseCode,
            licenseType,
            expireDate,
            count,
            customData,
        } = values;
        const parsedCount = parseInt(count);

        const editedLicense: License = {
            softwareLicenseCode,
            licenseType,
            expireDate: expireDate === '' ? null : expireDate,
            count: isNaN(parsedCount) ? null : parsedCount,
            customData,
        };

        if (license.id) {
            const payload = {
                integrationId,
                licenseId: license.id,
                licenseData: editedLicense,
            };
            dispatch(updateLicenseThunk(payload));
        } else {
            const payload = {
                accountId,
                integrationId,
                license: editedLicense,
            };
            dispatch(createLicenseThunk(payload));
        }
    };

    const formik = useFormik({
        initialValues: {
            ...license,
            count: license.count?.toString(),
            licenseType: license.licenseType || licenseTypes[0],
        },
        validationSchema,
        onSubmit: handleOnSubmit,
    });

    useEffect(() => {   
        if (createOrUpdateLicenseStatus === Status.Finished && error == '') {   
            navigate(`/integrations/${integrationId}/licenses`);
            return;
        }
    }, [createOrUpdateLicenseStatus, integrationId, error, dispatch, navigate]);

    useEffect(() => {     
        return  () => {
            dispatch(resetCreateOrUpdateLicenseStatus());  
            dispatch(removeError());
        } 
    }, [dispatch])

    const handleCancelButtonPressed = () => {  
        dispatch(removeError());               
        navigate(`/integrations/${integrationId}/licenses`);
    };
    
    const isLoading = createOrUpdateLicenseStatus === Status.Pending;
    return (
        <>
            <LoaderOverlay isLoading={isLoading} />
            <form onSubmit={formik.handleSubmit}>
                <TextInput
                    label="Software License Code"
                    name="softwareLicenseCode"
                    disabled={isNewLicense === false}
                    value={formik.values.softwareLicenseCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.softwareLicenseCode &&
                        (formik.errors.softwareLicenseCode as string | string[])
                    }
                />

                <Select
                    label="License Type"
                    name="licenseType"
                    disabled={isNewLicense === false}
                    value={formik.values.licenseType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errors={
                        formik.touched.licenseType &&
                        (formik.errors.licenseType as string | string[])
                    }
                >
                    {licenseTypes.map((licenseType) => (
                        <option key={licenseType} value={licenseType}>
                            {licenseType}
                        </option>
                    ))}
                </Select>

                <TextInput
                    label="Count"
                    name="count"
                    value={formik.values.count}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.count &&
                        (formik.errors.count as string | string[])
                    }
                />

                <TextInput
                    type="date"
                    label="Expire Date"
                    name="expireDate"
                    value={formik.values.expireDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.expireDate &&
                        (formik.errors.expireDate as string | string[])
                    }
                />

                <TextInput
                    label="Custom Data"
                    name="customData"
                    value={formik.values.customData}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.customData &&
                        (formik.errors.customData as string | string[])
                    }
                />
                {error!=="" && <div className="msds-text-body-4 msds-text-danger-red-1">{error}</div>}                
                <div className="msds-btn-group mt-6">
                    <Button color="primary" type="submit">
                        Save
                    </Button>
                    <Button
                        color="secondary"
                        onClick={handleCancelButtonPressed}
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </>
    );
}

type LicenseFormikValues = Omit<License, 'count'> & { count: string };

type LicenseEditorProps = {
    integration: Integration;
    license: License;
    isNewLicense: boolean;
};
